import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { registerSW } from 'virtual:pwa-register'
import { App } from './App'
import { appliquerLangue, langueDeLUrl, langueInitiale, urlDansLaLangue } from './i18n'
import { appliquerTheme, themeInitial } from './theme'

import './styles.css'

/**
 * La langue vit dans l'URL — `/fr/...` et `/ar/...`.
 *
 * **Pourquoi c'est une exigence de produit et non un confort.** L3 a fait des URL le support du
 * partage : « une recherche enregistrée est littéralement une URL ». Une URL qui ne porte pas sa
 * langue s'ouvre en français chez quelqu'un qui lit l'arabe — le lien partagé dans un groupe
 * arabophone arrive dans la mauvaise langue, et personne ne comprend pourquoi.
 *
 * **Le préfixe passe par `basename`, et pas par une route `/:langue`.** Les deux donnent la même
 * URL ; la seconde aurait imposé de réécrire les trente routes et chaque `<Link>` du produit, donc
 * d'introduire trente occasions d'en oublier un — juste avant une refonte graphique qui va toucher
 * tous ces fichiers. Avec `basename`, `to="/recherche"` résout de lui-même vers `/ar/recherche`.
 */
const langue = langueDeLUrl(window.location.pathname) ?? langueInitiale()

// Une URL sans préfixe est redirigée AVANT le premier rendu — `replace` et non `assign` : un
// retour arrière qui reviendrait sur l'URL sans langue rejouerait la redirection en boucle.
if (langueDeLUrl(window.location.pathname) === null) {
  window.history.replaceState(null, '', urlDansLaLangue(langue, window.location.pathname))
}

// La direction est posee AVANT le premier rendu : un basculement visible apres coup
// serait percu comme un defaut, et surtout il signalerait que le RTL est un ajout.
appliquerLangue(langue)

// Le theme, pour la meme raison, et une de plus : une page qui s'affiche en clair avant de
// basculer en sombre produit un eclair blanc dans le noir, que l'oeil retient bien plus qu'un
// changement de direction. React monte apres ; l'attribut est deja pose.
appliquerTheme(themeInitial())

registerSW({ immediate: true })

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {/*
      BrowserRouter et non HashRouter : les URL doivent etre de vraies URL, partageables et
      indexables. Le service worker sert deja l'application sur n'importe quel chemin
      (navigateFallback), et le nginx de l'hote fait de meme en production — sans quoi un lien
      partage vers /annonces/<id> rendrait un 404 au premier chargement.
    */}
    <BrowserRouter basename={`/${langue}`}>
      <App langueInitiale={langue} />
    </BrowserRouter>
  </StrictMode>,
)
